import { PersistentTierCounter, Pot } from '@1x2network/false_persistence'
import { BezierCurve, ParticleTrail, ParticleTrailSpecification } from '@1x2network/particle_trail'
import { Component, ContainerSpecification } from '@1x2network/pixi_assets'
import { StoredProperty } from '@1x2network/stored_settings'
import { searchToRecord } from '@1x2network/url_parameter_utils'
import { Container } from 'pixi.js'
import { CustomSymbol } from '../CustomSymbol'
import { ShapeOptions } from '../types/ShapeOptions'
import { makeShape } from '../utils/shapes/makeShape'
import { TweenVars } from '@1x2network/tweens'
import { flow } from '@1x2network/flow'

interface FalsePersistenceStorage {
    tier: number
    countdown: number
}

/**
 * Handles the false persistence mechanic for the logo and animating symbol collections
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class FalsePersistence extends Component<FalsePersistenceSpecification> {
    private pot: Pot
    private tierCounter: PersistentTierCounter

    constructor(pot: Pot, parent: Container) {
        super({ spec: 'falsePersistence', parent })

        const { gameID, playMode, sessionID } = searchToRecord({
            config: ['sessionID', 'gameID', 'playMode'],
        }) as { sessionID: string; gameID: string; playMode: string }

        const key = `${gameID}-${sessionID}-${playMode}_logo`
        const storage = new StoredProperty<FalsePersistenceStorage>(key)
        this.tierCounter = new PersistentTierCounter(storage, { values: this.specification.upgradeThresholds })

        this.pot = pot
        this.tierCounter.onTierChange.connect((tier) => {
            this.pot.setTier(tier)
        })
    }

    public async setCurrentTier() {
        for (let i = 0; i <= this.tierCounter.tier; i++) {
            this.pot.setTier(i)
            await flow.wait(this.specification.initialAnimTime)
        }
    }

    public setInitialBonus(): void {
        this.pot.reset()
        this.pot.setTier(this.tierCounter.maxTier - 1)
    }

    public animateMax(): void {
        this.pot.setTier(this.tierCounter.maxTier)
    }

    public async collect(symbol: CustomSymbol): Promise<void> {
        const startPosition = symbol.position
        const endPosition = this.pot.collectPosition
        const { trailSpec } = this.specification
        const { steps, tweenVars, shape: shapeOptions } = this.specification.trailAnim
        const shape = makeShape(shapeOptions.kind, shapeOptions.options)
        const points = shape.calculateControlPoints(startPosition, endPosition)
        const trail = new ParticleTrail(new BezierCurve(points, steps), trailSpec)
        const symbolPromise = symbol.playCollect()
        this.addChild(trail)
        const trailPromise = trail.animate({
            ...tweenVars,
            onParticlesComplete: () => {
                trail.destroy()
            },
            onComplete: () => {
                this.pot.playWin()
                this.tierCounter.trigger()
            },
        })
        await Promise.all([symbolPromise, trailPromise])
    }

    public reset(): void {
        if (this.tierCounter.tier === 0) {
            this.pot.setTier(0)
        }
        this.tierCounter.reset()
    }
}

export interface FalsePersistenceSpecification extends ContainerSpecification {
    upgradeThresholds: number[]
    initialAnimTime: number
    trailSpec: ParticleTrailSpecification
    trailAnim: {
        shape: ShapeOptions
        steps: number
        tweenVars: TweenVars
    }
}
